// « Aide et extension » de Radar (refonte du 2026-10-15, rapport radar-aide).
//
// POURQUOI CE N'EST PLUS LA PRISE EN MAIN. La page de la console (PriseEnMain)
// est une liste d'inscription en quatre étapes : CV, extension, connexion,
// sites. En Radar, l'inscription en trois écrans fait déjà le CV et les
// employeurs avant qu'on arrive ici ; ces étapes ne servaient plus qu'à
// repousser l'extension vers le bas. On vient sur cette page pour deux choses :
// l'extension, et quand quelque chose coince.
//
// L'EXTENSION SE DIT PAR SON ÉTAT, pas par des coches. Trois états, déduits du
// jeton (`/api/extension/token`) : pas connectée, connectée sans rien envoyé,
// connectée et utilisée. Radar demande aussi à l'extension du store si elle est
// là (issue #234, `lib/extensionInstallee.ts`) ; pour les autres, il ne sait que
// si elle est connectée : l'état « pas connectée » couvre donc les deux cas, d'où
// le lien « Déjà installée ? La connecter ». L'ancienne page cochait installer
// et connecter séparément, et montrait souvent « 3 faite, 2 à faire ».
//
// AU TÉLÉPHONE, on n'installe rien : Chrome n'y accepte pas les extensions.
// Même test que la carte de l'accueil (`surOrdinateur`).
//
// LES RÉPONSES S'ADRESSENT : `#/aide?q=mails` ouvre la réponse sur les mails.
// On peut citer ce lien dans une réponse au formulaire.

import { useEffect, useRef, useState } from 'react'
import { BookOpen, MessageSquare } from 'lucide-react'
import { get } from '../lib/api'
import { useData, Loading } from '../components/common'
import { useT, type CleUi } from '../lib/i18n'
import { useMots } from '../lib/vocabulaire'
import { navigate, parseQuery } from '../lib/router'
import { jourCourt, parseDate } from '../lib/format'
import { BoutonConnecterExtension, BoutonWebStore, InstallerParZip, surOrdinateur } from '../components/installerExtension'
import { useExtensionInstallee } from '../lib/extensionInstallee'
import { DemoActiver, DemoConnecter, DemoEmployeur, DemoInstaller } from '../components/demosAide'
import { CodeExtension, RetourDeveloppeur } from './PriseEnMain'
import { QUESTIONS_AIDE, type QuestionAide } from '../lib/aide'

interface EtatJeton {
  actif: boolean
  lastUsedAt?: string | null
}


export function AideRadar() {
  const t = useT()
  const mots = useMots()
  const ordinateur = surOrdinateur()
  const jeton = useData(() => get<EtatJeton>('/api/extension/token'), [])
  // La connexion se fait AILLEURS (dans les options de l'extension) : sans
  // relecture, la page resterait « pas connectée » jusqu'au rechargement.
  const relire = jeton.reload
  useEffect(() => {
    const minuterie = setInterval(relire, 10000)
    return () => clearInterval(minuterie)
  }, [relire])

  // Connectée dès le chargement : la coche est posée. Connectée pendant qu'on
  // regarde : elle se dessine.
  const auChargement = useRef<boolean | null>(null)
  if (auChargement.current === null && jeton.data) auChargement.current = jeton.data.actif === true

  const q = parseQuery(window.location.hash).get('q')
  const ouverte = (QUESTIONS_AIDE as readonly string[]).includes(q ?? '') ? (q as QuestionAide) : null

  if (jeton.loading && !jeton.data) return <Loading label={t('common.loading')} />
  const connectee = jeton.data?.actif === true
  const dernier = parseDate(jeton.data?.lastUsedAt)

  return (
    <>
      <div className="bar">
        <div>
          <h3>
            <BookOpen size={17} /> {mots('nav.aide')}
          </h3>
          <div className="sub">{t('aideR.intro')}</div>
        </div>
      </div>

      <div className="body aide aide-radar">
        <section className="card" id="aide-extension">
          <h3>{t('aideR.ext.titre')}</h3>
          {!ordinateur ? (
            <p className="aide-lead">{t('aideR.ext.telephone')}</p>
          ) : connectee ? (
            <ExtensionConnectee dernier={dernier} anime={auChargement.current === false} />
          ) : (
            <ExtensionAInstaller onConnectee={relire} />
          )}
        </section>

        <section className="card" id="aide-faq">
          <h3>{t('aideR.faq.titre')}</h3>
          <div className="aide-faq">
            <Question id="score" ouverte={ouverte}>
              {ordinateur ? (
                <>
                  <p>{t('aideR.r.score')}</p>
                  <DemoActiver />
                </>
              ) : (
                <p>{t('aideR.r.scoreTel')}</p>
              )}
            </Question>
            {ordinateur && (
              <Question id="reconnait" ouverte={ouverte}>
                <p>{t('aideR.r.reconnait')}</p>
                <CodeExtension />
              </Question>
            )}
            <Question id="employeur" ouverte={ouverte}>
              <p>{t('aideR.r.employeur')}</p>
              <DemoEmployeur />
              <Aller vers="#/sources" cle="aideR.cta.employeur" />
            </Question>
            <Question id="mails" ouverte={ouverte}>
              <p>{t('aideR.r.mails')}</p>
              <Aller vers="#/config?partie=compte" cle="aideR.cta.adresse" />
            </Question>
            <Question id="rythme" ouverte={ouverte}>
              <p>{t('aideR.r.rythme')}</p>
              <Aller vers="#/config?partie=ecrit" cle="aideR.cta.rythme" />
            </Question>
            <Question id="pause" ouverte={ouverte}>
              <p>{t('aideR.r.pause')}</p>
              <Aller vers="#/config?partie=compte" cle="aideR.cta.pause" />
            </Question>
            <Question id="supprimer" ouverte={ouverte}>
              <p>{t('aideR.r.supprimer')}</p>
              <Aller vers="#/config?partie=compte" cle="aideR.cta.compte" />
            </Question>
          </div>
        </section>

        <section className="card">
          <h3>{t('aideR.prive.titre')}</h3>
          <p className="etapec">{t('aideR.prive.texte')}</p>
        </section>

        <section className="card">
          <h3>
            <MessageSquare size={16} /> {t('aideR.ecrire.titre')}
          </h3>
          <p className="etapec">{t('aideR.ecrire.texte')}</p>
          <RetourDeveloppeur />
        </section>
      </div>
    </>
  )
}

/** Pas connectée : installer, ou connecter si c'est déjà installé. */
function ExtensionAInstaller({ onConnectee }: { onConnectee: () => void }) {
  const t = useT()
  const [vue, setVue] = useState<'installer' | 'connecter'>('installer')
  // Déjà installée (issue #234) : Radar le sait, et la connecte d'un clic.
  const installee = useExtensionInstallee()
  if (installee) {
    return (
      <>
        <p className="aide-lead">{t('ext.installee')}</p>
        <div className="aide-lien">
          <BoutonConnecterExtension onConnectee={onConnectee} />
        </div>
      </>
    )
  }
  return (
    <>
      <p className="aide-lead">{t('aideR.ext.lead')}</p>
      {vue === 'installer' ? (
        <>
          <DemoInstaller />
          <div className="aide-lien">
            <BoutonWebStore />
            <button type="button" className="linkbtn" onClick={() => setVue('connecter')}>
              {t('aideR.ext.dejaInstallee')}
            </button>
          </div>
          <details className="installer-zip">
            <summary>{t('aide.step.install.zipTitre')}</summary>
            <InstallerParZip />
          </details>
        </>
      ) : (
        <>
          <DemoConnecter />
          <p className="field-help">{t('aideR.ext.pasDeBouton')}</p>
          <CodeExtension />
          <div className="aide-lien">
            <button type="button" className="linkbtn" onClick={() => setVue('installer')}>
              {t('aideR.ext.pasInstallee')}
            </button>
          </div>
        </>
      )}
    </>
  )
}

/**
 * Connectée. La coche se DESSINE quand la connexion arrive pendant qu'on
 * regarde la page (relue toutes les 10 secondes) : c'est la seule animation
 * qui répond à un vrai changement. Déjà connectée au chargement, elle est
 * posée sans mouvement.
 */
function ExtensionConnectee({ dernier, anime }: { dernier: Date | null; anime: boolean }) {
  const t = useT()
  const [fait, setFait] = useState(!anime)
  useEffect(() => {
    if (!anime) return
    // Un cadre plus tard, pour que la transition parte de la coche vide.
    const id = requestAnimationFrame(() => setFait(true))
    return () => cancelAnimationFrame(id)
  }, [anime])
  return (
    <>
      <div className={`aide-etat${fait ? ' fait' : ''}`} role="status">
        <svg viewBox="0 0 26 26" fill="none" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="13" cy="13" r="11" />
          <path d="M8 13.5l3.5 3.5L18.5 10" />
        </svg>
        <span className="txt">
          <b>{t('aideR.ext.connectee')}</b>
          <small>{dernier ? t('aideR.ext.dernierEnvoi', { date: jourCourt(dernier) }) : t('aideR.ext.rienEnvoye')}</small>
        </span>
      </div>
      {!dernier && (
        <>
          <p className="aide-lead">{t('aideR.ext.ailleurs')}</p>
          <DemoActiver />
        </>
      )}
    </>
  )
}

function Question({ id, ouverte, children }: { id: QuestionAide; ouverte: QuestionAide | null; children: React.ReactNode }) {
  const t = useT()
  const ref = useRef<HTMLDetailsElement>(null)
  useEffect(() => {
    if (ouverte === id) ref.current?.scrollIntoView({ block: 'start' })
  }, [ouverte, id])
  return (
    <details ref={ref} id={`aide-${id}`} open={ouverte === id || undefined}>
      <summary>{t(`aideR.q.${id}` as CleUi)}</summary>
      <div className="rep">{children}</div>
    </details>
  )
}

function Aller({ vers, cle }: { vers: string; cle: CleUi }) {
  const t = useT()
  return (
    <div>
      <button type="button" className="btn-secondary" onClick={() => navigate(vers)}>
        {t(cle)}
      </button>
    </div>
  )
}
